// AAL-86 MyServices Copilot: the attachments of one turn (ticket 07).
//
// The user drops PDFs, images and text files into the drawer. This module holds their state as
// plain data: the file read as base64, its kind (what the agent service can do with it), the
// "add to Service images" toggle and, after the dashboard uploaded an image through the media
// endpoint, the media id the agent may append to the Service's images. The toggle is opt-in (off for
// every file): ticking it uploads the image to the Service Provider's media library before Save (the
// one documented exception to "nothing persists before Save") and is the user's usage consent. Caps mirror the Freshdesk
// Copilot defaults (5 files per turn, 5 MB each) and the agent service's request validation.
// Pure module: no store, no Vue, no HTTP; the page adapter (ServiceDesignerCopilot.js) does the
// upload and sends `toWire()` as the `attachments` of the propose body.

export const MAX_ATTACHMENTS = 5
export const MAX_ATTACHMENT_BYTES = 5_000_000

export type AttachmentKind = 'pdf' | 'image' | 'text' | 'other'
export type AttachmentStatus = 'ready' | 'uploading' | 'uploaded' | 'failed'
export type RejectReason = 'tooMany' | 'tooLarge' | 'duplicate'

export type PendingAttachment = {
	/** local id for the chips */
	id: string
	name: string
	mediaType: string
	size: number
	/** base64 body (no data URL prefix) */
	data: string
	kind: AttachmentKind
	/**
	 * images only, off until the user ticks it: upload to the Service Provider's media before the turn so
	 * the agent may append it. The tick is also the usage consent the upload sends.
	 */
	addToImages: boolean
	status: AttachmentStatus
	/** set after the upload: the media entry the agent may append to the Service's images */
	mediaId?: string | null
	/** whether the alt-text generator ran for the uploaded media (client feature `AI-alt-text-generation`) */
	hasAltText?: boolean | null
	error?: string
}

/** The agent service's shape: `attachments: [{name, mediaType, data, mediaId?, hasAltText?}]` */
export type WireAttachment = { name: string, mediaType: string, data: string, mediaId?: string, hasAltText?: boolean }

export type RejectedFile = { name: string, reason: RejectReason }

const IMAGE_TYPES = [ 'image/png', 'image/jpeg', 'image/gif', 'image/webp' ]
const PDF_TYPES = [ 'application/pdf', 'application/x-pdf' ]
const TEXT_TYPES = [ 'text/plain', 'text/csv', 'text/markdown', 'text/x-markdown', 'application/json' ]
const EXTENSION_TYPES: Record<string, string> = {
	'.pdf': 'application/pdf',
	'.png': 'image/png', '.jpg': 'image/jpeg', '.jpeg': 'image/jpeg', '.gif': 'image/gif', '.webp': 'image/webp',
	'.txt': 'text/plain', '.csv': 'text/csv', '.md': 'text/markdown', '.json': 'application/json',
}

/** `accept` of the file picker: the types the agent service reads */
export const ACCEPT = [ ...PDF_TYPES.slice(0, 1), ...IMAGE_TYPES, ...TEXT_TYPES.slice(0, 3), '.pdf', '.png', '.jpg', '.jpeg', '.gif', '.webp', '.txt', '.csv', '.md' ].join(',')

let counter = 0

// ---------------------------------------------------------------------------------------
// Kinds
// ---------------------------------------------------------------------------------------

export function mediaTypeOf(name: string, declared: string): string {
	const type = (declared || '').split(';')[0].trim().toLowerCase()
	if (type && type !== 'application/octet-stream') return type
	const dot = name.lastIndexOf('.')
	const extension = dot >= 0 ? name.slice(dot).toLowerCase() : ''
	return EXTENSION_TYPES[extension] ?? type
}

export function kindOf(name: string, declared: string): AttachmentKind {
	const type = mediaTypeOf(name, declared)
	if (PDF_TYPES.includes(type)) return 'pdf'
	if (IMAGE_TYPES.includes(type)) return 'image'
	if (TEXT_TYPES.includes(type)) return 'text'
	return 'other'
}

// ---------------------------------------------------------------------------------------
// Adding, toggling, removing
// ---------------------------------------------------------------------------------------

/**
 * Reads the files and appends them to `current` (a new list; the input is not mutated). A file over
 * the size cap, a name already in the list and every file beyond the count cap is reported in
 * `rejected` with its reason, so the drawer can say why it is missing.
 */
export async function addFiles(current: PendingAttachment[], files: Iterable<File>): Promise<{ attachments: PendingAttachment[], rejected: RejectedFile[] }> {
	const attachments = [ ...current ]
	const rejected: RejectedFile[] = []
	for (const file of files) {
		if (file.size > MAX_ATTACHMENT_BYTES) { rejected.push({ name: file.name, reason: 'tooLarge' }); continue }
		if (attachments.some(a => a.name === file.name)) { rejected.push({ name: file.name, reason: 'duplicate' }); continue }
		if (attachments.length >= MAX_ATTACHMENTS) { rejected.push({ name: file.name, reason: 'tooMany' }); continue }
		const mediaType = mediaTypeOf(file.name, file.type)
		const kind = kindOf(file.name, file.type)
		attachments.push({
			id: `att-${Date.now().toString(36)}-${++counter}`,
			name: file.name,
			mediaType,
			size: file.size,
			data: bytesToBase64(new Uint8Array(await file.arrayBuffer())),
			kind,
			addToImages: false,
			status: 'ready',
		})
	}
	return { attachments, rejected }
}

export function setAddToImages(attachments: PendingAttachment[], id: string, on: boolean): PendingAttachment[] {
	return attachments.map(a => (a.id === id && a.kind === 'image' ? { ...a, addToImages: on } : a))
}

export function removeAttachment(attachments: PendingAttachment[], id: string): PendingAttachment[] {
	return attachments.filter(a => a.id !== id)
}

// ---------------------------------------------------------------------------------------
// The wire shape and the media upload
// ---------------------------------------------------------------------------------------

export function toWire(attachments: PendingAttachment[]): WireAttachment[] {
	return attachments.map(a => {
		const wire: WireAttachment = { name: a.name, mediaType: a.mediaType, data: a.data }
		if (a.mediaId) {
			wire.mediaId = a.mediaId
			wire.hasAltText = a.hasAltText === true
		}
		return wire
	})
}

export type UploadContext = {
	serviceProviderId: string
	serviceProviderName: string
	/** locale of the title (the file name) and of the description */
	locale: string
	/** `{ [locale]: text }` from the alt-text generator, when the client feature is on */
	altText?: Record<string, string> | null
}

/**
 * The body of `POST /api/media` as components/media/MediaViewer.vue sends it (JSON with the file as a
 * data URL, not multipart). The title is the file name, so the created entry can be found again:
 * the controller returns no ids. The copyright owner is the Service Provider, as the media dialog
 * requires one (the toggle's label says so); usage consent is the user's own tick of "add to Service
 * images", passed through as it is, never assumed.
 */
export function uploadBody(attachment: PendingAttachment, context: UploadContext) {
	return {
		mediaAssets: [ {
			fields: {
				title: { [context.locale]: attachment.name },
				altText: context.altText && Object.keys(context.altText).length ? { ...context.altText } : { [context.locale]: '' },
				description: { [context.locale]: '' },
				file: { dataURL: `data:${attachment.mediaType};base64,${attachment.data}`, upload: { filename: attachment.name } },
			},
		} ],
		serviceProviderId: context.serviceProviderId,
		serviceProviderName: context.serviceProviderName,
		copyrightOwner: context.serviceProviderName,
		usageConsent: attachment.addToImages === true,
	}
}

type MediaEntry = { sys: { id: string }, fields?: any }

/**
 * The media entry the upload created, from the provider's media list before and after: an entry
 * that is new and titled after the file; when only one entry is new, that one. Several new entries
 * and no title match is undefined rather than a guess.
 */
export function findUploadedMedia(before: MediaEntry[], after: MediaEntry[], attachment: PendingAttachment): MediaEntry | undefined {
	const known = new Set(before.map(m => m?.sys?.id))
	const fresh = after.filter(m => m?.sys?.id && !known.has(m.sys.id))
	const titled = fresh.filter(m => Object.values(m.fields?.title ?? {}).includes(attachment.name))
	if (titled.length === 1) return titled[0]
	if (titled.length === 0 && fresh.length === 1) return fresh[0]
	return undefined
}

// ---------------------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------------------

export function bytesToBase64(bytes: Uint8Array): string {
	let binary = ''
	const chunk = 0x8000
	for (let i = 0; i < bytes.length; i += chunk) binary += String.fromCharCode(...bytes.subarray(i, i + chunk))
	return btoa(binary)
}

export function decodedSize(base64: string): number {
	if (!base64) return 0
	const padding = base64.length - base64.replace(/=+$/, '').length
	return Math.floor(base64.length * 3 / 4) - padding
}
